/**
 * Scroll behaviour: the reveal-on-enter transitions and the pinned product
 * section, lifted from the design canvas and given the guards a shipped page
 * needs — a reduced-motion opt-out, a rAF gate, and a fail-open timer.
 */

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const clamp = value => Math.min(1, Math.max(0, value));
/* Fast out, long settle: the curve the rest of the page's transitions use too. */
const easeOut = t => 1 - Math.pow(1 - t, 3);

/**
 * Fades elements in as they enter the viewport.
 *
 * The safety net matters more than the effect: `[data-reveal]` starts at
 * `opacity: 0`, so anything the observer misses is an invisible section. The
 * timer reveals whatever is left after 1.2s, which turns a broken observer into
 * a missing animation rather than a blank page.
 */
export function revealOnScroll(root) {
  const targets = root.querySelectorAll('[data-reveal]');
  if (!targets.length) return () => {};

  const reveal = el => {
    if (el.classList.contains('is-in')) return;
    el.classList.add('is-in');
  };

  if (reduced() || typeof IntersectionObserver !== 'function') {
    targets.forEach(reveal);
    return () => {};
  }

  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const step = parseInt(entry.target.getAttribute('data-reveal') || '1', 10);
      entry.target.style.transitionDelay = ((step - 1) * 0.075) + 's';
      reveal(entry.target);
      observer.unobserve(entry.target);
    }
  }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });

  targets.forEach(el => {
    observer.observe(el);
    // Anything already on screen at load reveals now rather than on a scroll
    // that may never come — on a short page, nothing ever intersects.
    const box = el.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) reveal(el);
  });

  const failOpen = setTimeout(() => {
    root.querySelectorAll('[data-reveal]:not(.is-in)').forEach(reveal);
  }, 1200);

  return () => {
    clearTimeout(failOpen);
    observer.disconnect();
  };
}

/**
 * The pinned section: three copy panels cross-fading as the page scrolls
 * through a 300vh spacer, with the phone scaling alongside.
 *
 * Below 900px the stylesheet un-pins the section and stacks the panels, so this
 * does nothing there — hence the media query check rather than a JS breakpoint,
 * which would be a second place for the number 900 to live.
 */
export function pinnedSection(root, { panelCount = 3, onPanel = () => {} } = {}) {
  const wrap = root.querySelector('[data-pin]');
  if (!wrap) return () => {};

  const panels = [...root.querySelectorAll('[data-panel]')];
  const dots = [...root.querySelectorAll('[data-dot]')];
  const phone = root.querySelector('[data-phone]');
  const fit = root.querySelector('[data-phone-fit]');
  const stage = root.querySelector('.pin__stage');
  const head = root.querySelector('.pin__head');
  const shot = root.querySelector('[data-shot]');

  const stacked = () => window.matchMedia('(max-width: 900px)').matches;

  /**
   * Everything in the pinned stage that is not the phone: its own padding, the
   * heading above the grid, and the gap between them.
   */
  function stageChrome() {
    if (!stage) return 200;
    const style = getComputedStyle(stage);
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const gap = head ? parseFloat(style.rowGap || style.gap) || 0 : 0;
    /* 64 for the live badge that sits above the phone inside its own column. */
    return padding + (head ? head.offsetHeight + gap : 0) + 64;
  }

  let shown = -1;

  /** Shows one panel and drops the rest, without touching the DOM structure. */
  function activate(index) {
    /* The live phone follows the panel -- told once per change, not per frame. */
    if (index !== shown) {
      shown = index;
      onPanel(index);
    }
    panels.forEach((panel, i) => {
      const on = i === index;
      panel.classList.toggle('is-on', on);
      /*
       * The active panel's inline transform must be *cleared*, not left to the
       * `.is-on { transform: none }` rule — an inline style outranks a class, so
       * a panel that had been nudged out of the way stayed nudged when its turn
       * came, sitting 28px low and riding down over the progress bars. It only
       * showed after the first transition, which is why it reads as a bug in the
       * animation rather than in the layout.
       */
      panel.style.transform = on ? '' : `translateY(${i < index ? -28 : 28}px)`;
      panel.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-on', i === index);
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  let frame = null;

  function paint() {
    frame = null;

    if (stacked()) {
      // Stacked layout: every panel is visible, and nothing is transformed.
      panels.forEach(panel => {
        panel.classList.add('is-on');
        panel.style.transform = '';
        panel.setAttribute('aria-hidden', 'false');
      });
      if (phone) phone.style.transform = '';
      if (fit) { fit.style.height = ''; fit.style.width = ''; }
      if (shot) shot.style.transform = '';
      return;
    }

    const box = wrap.getBoundingClientRect();
    const span = Math.max(1, box.height - window.innerHeight);
    const progress = Math.min(1, Math.max(0, -box.top / span));

    activate(Math.min(panelCount - 1, Math.floor(progress * panelCount)));

    if (phone) {
      /*
       * The phone is 714px tall, which is taller than a laptop viewport once
       * the sticky padding is taken out. `fit` scales it down to whatever is
       * actually available, and the wrapper is resized to match so the grid
       * column reserves the space the *scaled* phone occupies, not the space
       * the unscaled one would.
       *
       * What is above the phone is measured rather than assumed. It was a flat
       * 200, and the moment a heading was added over the stage the sum stopped
       * being true: the column grew taller than the box it is centred in, so
       * centring pushed it past the padding and the heading went up under the
       * navigation bar. Measuring also handles the heading wrapping to two
       * lines on a narrow screen, which no constant could.
       */
      const chrome = stageChrome();
      const available = Math.min(1, (window.innerHeight - chrome) / 714);
      const scale = (0.9 + Math.min(0.1, progress * 0.28)) * available;
      phone.style.transformOrigin = 'center center';
      phone.style.transform = `scale(${scale.toFixed(3)}) rotateX(${((1 - progress) * 4).toFixed(2)}deg)`;
      if (fit) {
        fit.style.height = Math.round(714 * available) + 'px';
        fit.style.width = Math.round(330 * available) + 'px';
      }
    }

    if (shot) {
      /*
       * The window rises into place as it comes up the screen: tilted back and
       * a size smaller at first, flat and full-size by the time its top is a
       * fifth of the way down. Eased, so it settles rather than stops.
       */
      const box2 = shot.getBoundingClientRect();
      const t = easeOut(clamp((window.innerHeight - box2.top) / (window.innerHeight * 0.8)));
      shot.style.transformOrigin = 'center bottom';
      shot.style.transform =
        `perspective(2400px) rotateX(${((1 - t) * 16).toFixed(2)}deg) ` +
        `scale(${(0.86 + t * 0.14).toFixed(3)}) translateY(${((1 - t) * 36).toFixed(1)}px)`;
    }
  }

  if (reduced()) {
    // No scroll choreography, but the panels still have to be readable: show
    // them all, exactly as the stacked layout does. The phone stays on its
    // first screen.
    onPanel(0);
    panels.forEach(panel => { panel.classList.add('is-on'); panel.setAttribute('aria-hidden', 'false'); });
    dots.forEach(dot => dot.classList.add('is-on'));
    return () => {};
  }

  const onScroll = () => { if (!frame) frame = requestAnimationFrame(paint); };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  paint();

  return () => {
    if (frame) cancelAnimationFrame(frame);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}

/**
 * A paragraph that lights up a word at a time as it scrolls through the
 * middle of the screen -- read at the pace the page is moved, not all at once.
 *
 * The words are wrapped in spans once; the text itself is unchanged, so a
 * screen reader and a copy-paste get the sentence as written. With reduced
 * motion, or before the script runs, the paragraph is simply fully lit.
 */
export function highlightOnScroll(root) {
  const targets = [...root.querySelectorAll('[data-highlight]')];
  if (!targets.length || reduced()) return () => {};

  const lit = targets.map(el => {
    const text = el.textContent;
    const spans = text.split(/(\s+)/).map(part => {
      if (/^\s+$/.test(part) || !part) return document.createTextNode(part);
      const span = document.createElement('span');
      span.className = 'hl';
      span.textContent = part;
      return span;
    });
    el.replaceChildren(...spans);
    el.classList.add('is-highlighting');
    return { el, words: [...el.querySelectorAll('.hl')] };
  });

  let frame = null;
  const paint = () => {
    frame = null;
    const vh = window.innerHeight;
    for (const { el, words } of lit) {
      const box = el.getBoundingClientRect();
      /* From its top reaching the lower fifth of the screen to its bottom passing the middle. */
      const progress = clamp((vh * 0.82 - box.top) / (box.height + vh * 0.32));
      const upto = Math.round(progress * words.length);
      words.forEach((word, i) => word.classList.toggle('is-lit', i < upto));
    }
  };
  const onScroll = () => { if (!frame) frame = requestAnimationFrame(paint); };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  paint();

  return () => {
    if (frame) cancelAnimationFrame(frame);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}

/**
 * The screen switcher under the live desktop preview.
 *
 * While it is on screen it moves to the next screen every few seconds, with a
 * bar on the active button filling up to show when. The first press stops
 * that for good: from then on the reader decides what is shown.
 */
export function previewSwitcher(root, { onPick, every = 5200 } = {}) {
  const bar = root.querySelector('[data-switcher]');
  if (!bar) return () => {};
  const buttons = [...bar.querySelectorAll('[data-preview-tab]')];
  let index = 0;
  let timer = null;
  let visible = false;
  let chosen = false;

  const select = i => {
    index = (i + buttons.length) % buttons.length;
    buttons.forEach((button, n) => {
      const on = n === index;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      /* Restart the fill: drop the class, force a style pass, put it back. */
      button.classList.remove('is-auto');
    });
    if (!chosen && visible && !reduced()) {
      void buttons[index].offsetWidth;
      buttons[index].classList.add('is-auto');
    }
    onPick(buttons[index].getAttribute('data-preview-tab'));
  };

  const stop = () => { clearInterval(timer); timer = null; };
  const run = () => {
    stop();
    if (chosen || !visible || reduced()) return;
    buttons[index].classList.add('is-auto');
    timer = setInterval(() => select(index + 1), every);
  };

  buttons.forEach((button, i) => button.addEventListener('click', () => {
    chosen = true;
    stop();
    buttons.forEach(b => b.classList.remove('is-auto'));
    select(i);
  }));

  const observer = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) run();
      else { stop(); buttons.forEach(b => b.classList.remove('is-auto')); }
    }, { threshold: 0.35 })
    : null;
  observer?.observe(bar.closest('[data-live-host]') || bar);

  return () => {
    stop();
    observer?.disconnect();
  };
}
